<!-- Toast Messages - Positioned at root level -->
<p-toast
  position="top-right"
  [baseZIndex]="9999"
  [autoZIndex]="true"
  [showTransformOptions]="'translateX(100%)'"
  [hideTransformOptions]="'translateX(100%)'"
  [showTransitionOptions]="'0.3s ease-in'"
  [hideTransitionOptions]="'0.3s ease-out'"
  [preventDuplicates]="false"
></p-toast>

<div class="p-6">
  <!-- Header -->
  <h2 class="text-2xl font-semibold text-gray-800 mb-3">
    {{ "upload_detail_config.title" | translate }}
  </h2>

  <div class="settings-container">
    <!-- Left Sidebar Navigation -->
    <div class="settings-sidebar">
      <div class="sidebar-header">
        <div class="sidebar-title">
          {{ "upload_detail_config.sections.product_asset" | translate }}
        </div>
      </div>

      <div class="sidebar-content">
        <div class="sidebar-section">
          <button
            class="sidebar-item"
            [class.active]="selectedSection() === 'detail-images'"
            (click)="selectSection('detail-images')"
            type="button"
            [attr.aria-label]="
              'upload_detail_config.sections.detail_images' | translate
            "
          >
            <i class="pi pi-images sidebar-icon"></i>
            <span class="sidebar-label">
              {{ "upload_detail_config.sections.detail_images" | translate }}
            </span>
          </button>

          <button
            class="sidebar-item"
            [class.active]="selectedSection() === 'main-image'"
            (click)="selectSection('main-image')"
            type="button"
            [attr.aria-label]="
              'upload_detail_config.sections.main_image' | translate
            "
          >
            <i class="pi pi-image sidebar-icon"></i>
            <span class="sidebar-label">
              {{ "upload_detail_config.sections.main_image" | translate }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="settings-main">
      <div class="settings-content">
        @if (selectedSection() === "detail-images") {
          <div class="settings-section">
            <!-- Header Section -->
            <div class="header-section">
              <div class="section-header-with-actions">
                <h3 class="section-header">
                  {{
                    "upload_detail_config.sections.detail_images" | translate
                  }}
                </h3>
              </div>
              <p class="section-description">
                {{
                  "upload_detail_config.descriptions.detail_images" | translate
                }}
              </p>
            </div>

            <!-- Form Content -->
            <div class="form-content">
              <div class="settings-group">
                <!-- File Category -->
                <div class="setting-item">
                  <div class="setting-label">
                    <label for="detail-file-category">
                      {{
                        "upload_detail_config.form.fields.file_category"
                          | translate
                      }}
                    </label>
                    <span class="setting-description">
                      {{
                        "upload_detail_config.descriptions.file_category"
                          | translate
                      }}
                    </span>
                  </div>
                  <div class="setting-control">
                    <p-select
                      id="detail-file-category"
                      [options]="getTranslatedFileCategoryOptions()"
                      [(ngModel)]="detailImagesConfig.fileCategory"
                      [ngModelOptions]="{ standalone: true }"
                      optionLabel="label"
                      optionValue="value"
                      (onChange)="onFileCategoryChange(detailImagesConfig)"
                      class="w-full"
                    ></p-select>
                  </div>
                </div>

                <!-- File Format -->
                <div class="setting-item">
                  <div class="setting-label">
                    <label for="detail-file-format">
                      {{
                        "upload_detail_config.form.fields.file_format"
                          | translate
                      }}
                    </label>
                    <span class="setting-description">
                      {{
                        "upload_detail_config.descriptions.file_format"
                          | translate
                      }}
                    </span>
                  </div>
                  <div class="setting-control">
                    <p-multiSelect
                      id="detail-file-format"
                      [options]="
                        getTranslatedFileFormatOptions(
                          detailImagesConfig.fileCategory
                        )
                      "
                      [(ngModel)]="detailImagesConfig.fileFormat"
                      [ngModelOptions]="{ standalone: true }"
                      optionLabel="label"
                      optionValue="value"
                      [showClear]="true"
                      [displaySelectedLabel]="true"
                      [maxSelectedLabels]="10"
                      [placeholder]="
                        'upload_detail_config.form.placeholders.select_file_formats'
                          | translate
                      "
                      class="w-full"
                    ></p-multiSelect>
                  </div>
                </div>

                <!-- File Size Limit -->
                <div class="setting-item">
                  <div class="setting-label">
                    <label for="detail-file-size">
                      {{
                        "upload_detail_config.form.fields.file_size_limit"
                          | translate
                      }}
                    </label>
                    <span class="setting-description">
                      {{
                        "upload_detail_config.descriptions.file_size_limit"
                          | translate
                      }}
                    </span>
                  </div>
                  <div class="setting-control">
                    <div class="size-input-group">
                      <p-inputNumber
                        id="detail-file-size"
                        [(ngModel)]="detailImagesConfig.fileSizeLimit"
                        [ngModelOptions]="{ standalone: true }"
                        [min]="1"
                        [max]="10"
                        class="size-input"
                      ></p-inputNumber>
                      <p-select
                        [options]="getTranslatedSizeUnitOptions()"
                        [(ngModel)]="detailImagesConfig.sizeUnit"
                        [ngModelOptions]="{ standalone: true }"
                        optionLabel="label"
                        optionValue="value"
                        class="unit-select"
                      ></p-select>
                    </div>
                  </div>
                </div>

                <!-- Max Images Allowed -->
                <div class="setting-item">
                  <div class="setting-label">
                    <label for="detail-max-images">
                      {{
                        "upload_detail_config.form.fields.max_detail_images"
                          | translate
                      }}
                    </label>
                    <span class="setting-description">
                      {{
                        "upload_detail_config.descriptions.max_detail_images"
                          | translate
                      }}
                    </span>
                  </div>
                  <div class="setting-control">
                    <p-inputNumber
                      id="detail-max-images"
                      [(ngModel)]="detailImagesConfig.maxDetailImages"
                      [ngModelOptions]="{ standalone: true }"
                      [min]="1"
                      [max]="20"
                      class="w-full"
                    ></p-inputNumber>
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="action-buttons-section">
              <div class="form-action-buttons">
                <app-button
                  [label]="'common.actions.reset' | translate"
                  type="secondary"
                  [outlined]="true"
                  (emitOnClick)="resetConfigurations()"
                  class="reset-button"
                ></app-button>
                <app-button
                  [label]="'common.actions.save' | translate"
                  type="info"
                  (emitOnClick)="saveConfigurations()"
                  class="save-button"
                ></app-button>
              </div>
            </div>
          </div>
        }
        @if (selectedSection() === "main-image") {
          <div class="settings-section">
            <!-- Header Section -->
            <div class="header-section">
              <div class="section-header-with-actions">
                <h3 class="section-header">
                  {{ "upload_detail_config.sections.main_image" | translate }}
                </h3>
              </div>
              <p class="section-description">
                {{ "upload_detail_config.descriptions.main_image" | translate }}
              </p>
            </div>

            <!-- Form Content -->
            <div class="form-content">
              <div class="settings-group">
                <!-- File Category -->
                <div class="setting-item">
                  <div class="setting-label">
                    <label for="main-file-category">
                      {{
                        "upload_detail_config.form.fields.file_category"
                          | translate
                      }}
                    </label>
                    <span class="setting-description">
                      {{
                        "upload_detail_config.descriptions.file_category"
                          | translate
                      }}
                    </span>
                  </div>
                  <div class="setting-control">
                    <p-select
                      id="main-file-category"
                      [options]="getTranslatedFileCategoryOptions()"
                      [(ngModel)]="mainImageConfig.fileCategory"
                      [ngModelOptions]="{ standalone: true }"
                      optionLabel="label"
                      optionValue="value"
                      (onChange)="onFileCategoryChange(mainImageConfig)"
                      class="w-full"
                    ></p-select>
                  </div>
                </div>

                <!-- File Format -->
                <div class="setting-item">
                  <div class="setting-label">
                    <label for="main-file-format">
                      {{
                        "upload_detail_config.form.fields.file_format"
                          | translate
                      }}
                    </label>
                    <span class="setting-description">
                      {{
                        "upload_detail_config.descriptions.file_format"
                          | translate
                      }}
                    </span>
                  </div>
                  <div class="setting-control">
                    <p-multiSelect
                      id="main-file-format"
                      [options]="
                        getTranslatedFileFormatOptions(
                          mainImageConfig.fileCategory
                        )
                      "
                      [(ngModel)]="mainImageConfig.fileFormat"
                      [ngModelOptions]="{ standalone: true }"
                      optionLabel="label"
                      optionValue="value"
                      [showClear]="true"
                      [displaySelectedLabel]="true"
                      [maxSelectedLabels]="10"
                      [placeholder]="
                        'upload_detail_config.form.placeholders.select_file_formats'
                          | translate
                      "
                      class="w-full"
                    ></p-multiSelect>
                  </div>
                </div>

                <!-- File Size Limit -->
                <div class="setting-item">
                  <div class="setting-label">
                    <label for="main-file-size">
                      {{
                        "upload_detail_config.form.fields.file_size_limit"
                          | translate
                      }}
                    </label>
                    <span class="setting-description">
                      {{
                        "upload_detail_config.descriptions.file_size_limit"
                          | translate
                      }}
                    </span>
                  </div>
                  <div class="setting-control">
                    <div class="size-input-group">
                      <p-inputNumber
                        id="main-file-size"
                        [(ngModel)]="mainImageConfig.fileSizeLimit"
                        [ngModelOptions]="{ standalone: true }"
                        [min]="1"
                        [max]="10"
                        class="size-input"
                      ></p-inputNumber>
                      <p-select
                        [options]="getTranslatedSizeUnitOptions()"
                        [(ngModel)]="mainImageConfig.sizeUnit"
                        [ngModelOptions]="{ standalone: true }"
                        optionLabel="label"
                        optionValue="value"
                        class="unit-select"
                      ></p-select>
                    </div>
                  </div>
                </div>

                <!-- Max Images Allowed (Disabled for Main Image) -->
                <div class="setting-item">
                  <div class="setting-label">
                    <label for="main-max-images">
                      {{
                        "upload_detail_config.form.fields.max_detail_images"
                          | translate
                      }}
                    </label>
                    <span class="setting-description">
                      Main image is limited to 1 file only
                    </span>
                  </div>
                  <div class="setting-control">
                    <p-inputNumber
                      id="main-max-images"
                      [(ngModel)]="mainImageConfig.maxDetailImages"
                      [ngModelOptions]="{ standalone: true }"
                      [min]="1"
                      [max]="1"
                      [disabled]="true"
                      class="w-full"
                    ></p-inputNumber>
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="action-buttons-section">
              <div class="form-action-buttons">
                <app-button
                  [label]="'common.actions.reset' | translate"
                  type="secondary"
                  [outlined]="true"
                  (emitOnClick)="resetConfigurations()"
                  class="reset-button"
                ></app-button>
                <app-button
                  [label]="'common.actions.save' | translate"
                  type="info"
                  (emitOnClick)="saveConfigurations()"
                  class="save-button"
                ></app-button>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  </div>
</div>
